import axios, {
  Axios,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosRequestTransformer,
  AxiosResponse,
} from 'axios';
import { v4 as uuidv4 } from 'uuid';
import type TokenizedApi from '../TokenizedApi';
import { apiBaseUrls } from '../utils/backends';
import {
  reformatEndpointError,
  TokenizedApiError,
  TokenizedAuthError,
} from '../utils/errors';
import {
  resolvePlatformApiKeyAction,
  selectAuthToken,
  selectPreAuthToken,
  selectTokenNeedsRefresh,
} from './Account';
import type { TReduxState } from './ReduxState';
import Service from './Service';
import packageJson from '../../../package.json';

/**
 * A function that will be called whenever the SDK detects an error.
 * If you return a value from the function, that value
 * will be thrown instead of the original error.
 */
export type TErrorTransform = (error: Error) => Error | undefined;

export type TMobileAuthTokenCallback = () => Promise<string>;

export type TMobileAuthHeadersCallback = () => Promise<Record<string, string>>;

export type TRestoreAuthTokenCallback = () => Promise<string>;

type TAuthTokenSelector = ((state: TReduxState) => string | null) | null;

/** @internal */
class TokenizedApiEndpoints extends Service {
  readonly api: Axios;
  readonly apiV0?: Axios;
  readonly sse?: Axios;
  readonly errorTransform: TErrorTransform | null = null;
  readonly platformApiKey: string | null;
  readonly baseURL: string;

  mobileTokenRefreshInProgress?: Promise<string>;

  constructor(tokenizedApi: TokenizedApi) {
    super(tokenizedApi);
    const config = tokenizedApi.config;

    let baseURL = config.restApiBaseUrl;
    if (!baseURL) {
      if (
        !config.tokenizedBackend ||
        typeof config.tokenizedBackend !== 'string' ||
        !apiBaseUrls[config.tokenizedBackend]
      ) {
        throw new TypeError(
          `Invalid config: tokenizedBackend must be one of ${Object.keys(
            apiBaseUrls,
          )
            .map((backend) => `'${backend}'`)
            .join()}`,
        );
      }
      baseURL = apiBaseUrls[config.tokenizedBackend];
    } else {
      if (typeof baseURL !== 'string') {
        throw new TypeError(
          'Invalid config: restApiBaseUrl must be a url string or null',
        );
      }
    }
    if (config.errorTransform) {
      if (typeof config.errorTransform !== 'function') {
        throw new TypeError(
          'Invalid config: errorTransform must be a function or null',
        );
      }
      this.errorTransform = config.errorTransform;
    }

    const headers: AxiosRequestHeaders = {
      // Note: The browser is in charge of 'User-Agent'
      'x-user-agent': `tokenized-sdk-${packageJson.version}`,
    };
    if (config.userAgentString) {
      if (typeof config.userAgentString !== 'string') {
        throw new TypeError(
          'Invalid config: userAgentString must be a string or null',
        );
      }
      headers['x-user-agent'] =
        `${config.userAgentString} ${headers['x-user-agent']}`;
    }

    let platformApiKey: string | null = null;
    if (
      typeof config.platformApiKey === 'string' ||
      config.platformApiKey === null
    ) {
      platformApiKey = config.platformApiKey;
    } else if (typeof config.platformApiKey === 'object') {
      const selectedApiKey =
        config.tokenizedBackend &&
        config.platformApiKey[config.tokenizedBackend];
      if (selectedApiKey) {
        if (typeof selectedApiKey !== 'string' && selectedApiKey !== null) {
          throw new TypeError(
            `Invalid config: platformApiKey.${config.tokenizedBackend} must be a string or null`,
          );
        }
        platformApiKey = selectedApiKey;
      }
    } else {
      throw new TypeError(
        `Invalid config: platformApiKey must be a string, object, or null (got ${typeof config.platformApiKey})`,
      );
    }
    if (platformApiKey) {
      headers['x-api-key'] = platformApiKey;
    }
    this.platformApiKey = platformApiKey;
    tokenizedApi.state.dispatch(
      resolvePlatformApiKeyAction(this.platformApiKey),
    );

    this.baseURL = baseURL;

    this.api = axios.create({
      baseURL,
      headers,
    });
    const calculateHeaders: AxiosRequestTransformer = (data, headers) => {
      // Common headers that are recalculated for every request
      if (headers && !headers['x-trace']) {
        headers['x-trace'] = uuidv4();
      }
      return data;
    };
    if (this.api.defaults.transformRequest) {
      this.api.defaults.transformRequest = [
        ...(Array.isArray(this.api.defaults.transformRequest)
          ? this.api.defaults.transformRequest
          : [this.api.defaults.transformRequest]),
        calculateHeaders,
      ];
    } else {
      this.api.defaults.transformRequest = calculateHeaders;
    }
    // Install error handling for all endpoints
    const handleErrors = (endpointError: unknown) => {
      const sdkError = reformatEndpointError(endpointError, this);

      // Auth errors should cause a log out
      this.tokenizedApi.account.onErrorFromTokenizedApi(sdkError);

      let transformedError: Error = sdkError;
      if (this.errorTransform) {
        // The app can capture or transform errors before
        // they’re thrown (but it can’t suppress them)
        transformedError = this.errorTransform(sdkError) || sdkError;
      }
      throw transformedError;
    };
    this.api.interceptors.response.use((response) => response, handleErrors);

    // TODO: remove apiV0
    if (baseURL.endsWith('/v1')) {
      this.apiV0 = axios.create({
        baseURL: baseURL.slice(0, -3),
        headers,
      });
      if (this.apiV0.defaults.transformRequest) {
        this.apiV0.defaults.transformRequest = [
          ...(Array.isArray(this.apiV0.defaults.transformRequest)
            ? this.apiV0.defaults.transformRequest
            : [this.apiV0.defaults.transformRequest]),
          calculateHeaders,
        ];
      } else {
        this.apiV0.defaults.transformRequest = calculateHeaders;
      }
      this.apiV0.interceptors.response.use(
        (response) => response,
        handleErrors,
      );
    }

    if (config.sseBaseUrl) {
      this.sse = axios.create({ baseURL: config.sseBaseUrl });
    } else if (config.tokenizedBackend !== 'local') {
      this.sse = this.apiV0;
    }
  }

  async addAuthHeader<R>(
    config: AxiosRequestConfig<R> | undefined,
    authTokenSelector: TAuthTokenSelector = selectAuthToken,
  ) {
    let token: string | null = null;

    if (
      (authTokenSelector === selectAuthToken ||
        authTokenSelector === selectPreAuthToken) &&
      typeof this.tokenizedApi.config.mobileAuthToken === 'function'
    ) {
      // The mobile auth token is fetched/refreshed on demand
      token = authTokenSelector(this.tokenizedApi.getReduxState());
      const mobileTokenFunction = this.tokenizedApi.config.mobileAuthToken;
      if (
        !token ||
        selectTokenNeedsRefresh(this.tokenizedApi.getReduxState())
      ) {
        if (!this.mobileTokenRefreshInProgress) {
          this.mobileTokenRefreshInProgress = (async () => {
            try {
              const token = await mobileTokenFunction();
              if (!token) {
                throw new Error('No auth token');
              }
              this.tokenizedApi.account.supplyToken(token);
              return token;
            } catch (error) {
              if (error instanceof TokenizedApiError) {
                throw error;
              }
              console.error(error);
              throw new TokenizedAuthError('Account pairing invalid', {
                fromError: error,
              }).withFormatting(this);
            } finally {
              this.mobileTokenRefreshInProgress = undefined;
            }
          })();
        }
        token = await this.mobileTokenRefreshInProgress;
      }
    }

    if (authTokenSelector) {
      token = authTokenSelector(this.tokenizedApi.getReduxState());
      if (!token) {
        const error = new TokenizedAuthError().withFormatting(this);
        // This will cause the app to log out
        this.tokenizedApi.account.onErrorFromTokenizedApi(error);
        throw error;
      }
    }

    let mobileAuthHeaders;
    if (this.tokenizedApi.config?.mobileAuthHeaders) {
      mobileAuthHeaders = await this.tokenizedApi.config.mobileAuthHeaders();
    }

    if (token) {
      return {
        ...config,
        headers: {
          ...mobileAuthHeaders,
          ...config?.headers,
          Authorization: `Bearer ${token}`,
        },
      };
    }
    return config;
  }

  async get<R = { data: Record<string, unknown> }>(
    url: string,
    config?: AxiosRequestConfig,
    authTokenSelector?: TAuthTokenSelector,
  ): Promise<AxiosResponse<R>> {
    return this.api.get(
      url,
      await this.addAuthHeader(config, authTokenSelector),
    );
  }

  async put<R = { data: Record<string, unknown> }, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
    authTokenSelector?: TAuthTokenSelector,
  ): Promise<AxiosResponse<R>> {
    return this.api.put(
      url,
      data,
      await this.addAuthHeader(config, authTokenSelector),
    );
  }

  async post<R = { data: Record<string, unknown> }, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
    authTokenSelector?: TAuthTokenSelector,
  ): Promise<AxiosResponse<R>> {
    return this.api.post(
      url,
      data,
      await this.addAuthHeader(config, authTokenSelector),
    );
  }

  getSSEBaseUrl() {
    if (!this.sse) {
      throw new Error('SSE API not available for this base URL');
    }
    return this.sse.defaults.baseURL;
  }

  async postSSE<R = { data: Record<string, unknown> }, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
    authTokenSelector?: TAuthTokenSelector,
  ): Promise<AxiosResponse<R>> {
    if (!this.sse) {
      throw new Error('SSE not available for this base URL');
    }
    return this.sse.post(
      url,
      data,
      await this.addAuthHeader(config, authTokenSelector),
    );
  }

  async delete<R = unknown>(
    url: string,
    config?: AxiosRequestConfig,
    authTokenSelector?: TAuthTokenSelector,
  ): Promise<AxiosResponse<R>> {
    return this.api.delete(
      url,
      await this.addAuthHeader(config, authTokenSelector),
    );
  }

  getPlatformApiKey() {
    return this.platformApiKey;
  }

  selectPreAuthToken = selectPreAuthToken;
}

export default TokenizedApiEndpoints;
